"use client"

import * as React from "react"
// Replacing next/image with standard img tag
// Replacing next/link with react-router-dom Link
import { Link } from "react-router-dom"
import { cn } from "../../lib/utils"
import { Mockup, MockupFrame } from "./mockup"
import { AnimatedGridPattern } from "./animated-grid-pattern"
import { RainbowButton } from "./rainbow-button"

interface HeroProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode
  subtitle?: string
  eyebrow?: string
  ctaText?: string
  ctaLink?: string
  mockupImage?: {
    src: string
    alt: string
    width: number
    height: number
  }
  showGridPattern?: boolean
}

const Hero = React.forwardRef<HTMLDivElement, HeroProps>(
  ({ className, title, subtitle, eyebrow, ctaText, ctaLink, mockupImage, showGridPattern = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col items-center w-full overflow-x-hidden min-h-screen pt-20 relative", className)}
        {...props}
      >
        {showGridPattern && (
          <AnimatedGridPattern
            numSquares={20}
            maxOpacity={0.05}
            duration={2}
            repeatDelay={0.5}
            className={cn(
              "[mask-image:radial-gradient(800px_circle_at_center,white,transparent)]",
            )}
          />
        )}
        
        {eyebrow && (
          <p 
            className="font-instrument-sans uppercase tracking-[0.51em] leading-[133%] text-center text-[19px] mt-10 mb-8 text-[#000000] animate-appear opacity-0 relative z-10" // Reduced mt-[249px] to mt-10
          >
            {eyebrow}
          </p>
        )}

        <h1 
          className="text-5xl md:text-6xl lg:text-7xl leading-tight text-center px-4 max-w-6xl w-full animate-appear opacity-0 delay-100 relative z-10"
        >
          {title}
        </h1>

        {subtitle && (
          <p 
            className="text-xl md:text-2xl text-center font-instrument-sans font-light px-4 max-w-4xl w-full mt-[25px] mb-[48px] leading-[133%] text-[#000000] animate-appear opacity-0 delay-300 relative z-10"
          >
            {subtitle}
          </p>
        )}

        {ctaText && ctaLink && (
          <div className="animate-appear opacity-0 relative z-10">
            <RainbowButton href={ctaLink}>
              {ctaText}
            </RainbowButton>
          </div>
        )}

        {mockupImage && (
          <div className="mt-20 w-full relative animate-appear opacity-0 delay-700 z-10 flex justify-center">
            <MockupFrame className="w-[80%]">
              <Mockup type="responsive">
                {/* Replacing next/image with standard img tag and making it 20% smaller */}
                <img
                  src={mockupImage.src}
                  alt={mockupImage.alt}
                  width={mockupImage.width * 0.8} // 20% smaller
                  height={mockupImage.height * 0.8} // 20% smaller
                  className="w-full" // Full width within the constrained frame
                />
              </Mockup>
            </MockupFrame>
            {/* Removed the white gradient banner that was causing the white bar at the bottom */}
          </div>
        )}
      </div>
    )
  }
)
Hero.displayName = "Hero"

export { Hero }